import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminRegistrationOrdersAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { consumeAdminNavHandoff } from '../utils/adminNavFilters';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem 1.75rem 2rem;
  max-width: 1280px;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.25rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  max-width: 42rem;
  line-height: 1.45;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.25rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  padding: 1rem 1.15rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fafafa;
`;

const KpiLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const KpiValue = styled.div`
  margin-top: 0.35rem;
  font-size: 1.75rem;
  font-weight: 800;
  color: ${BRAND.blue};
  line-height: 1.1;
`;

const KpiHint = styled.div`
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: ${(p) => (p.$alert ? BRAND.red : '#6b7280')};
  font-weight: ${(p) => (p.$alert ? 600 : 500)};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  background: ${(p) => (p.$active ? BRAND.red : '#f3f4f6')};
  color: ${(p) => (p.$active ? '#fff' : '#374151')};
  &:hover {
    background: ${(p) => (p.$active ? BRAND.red : '#e5e7eb')};
  }
`;

const SearchInput = styled.input`
  flex: 1 1 180px;
  min-width: 160px;
  max-width: 280px;
  padding: 0.55rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #fff;
`;

const DateInput = styled.input`
  padding: 0.55rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #fff;
`;

const ExportButton = styled.button`
  margin-left: auto;
  background: ${BRAND.blue};
  color: #fff;
  border: none;
  padding: 0.5rem 0.95rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #0b4a86;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$withDetail ? 'minmax(0, 1fr) minmax(300px, 380px)' : '1fr')};
  gap: 1rem;
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    border-bottom: 1px solid #e5e7eb;
    padding: 0.85rem 0.9rem;
    text-align: left;
    vertical-align: top;
  }
  th {
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: #fafafa;
  }
  tbody tr {
    cursor: pointer;
    position: relative;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ActiveRow = styled.tr`
  background: ${(p) => (p.$active ? 'rgba(213, 43, 30, 0.06)' : 'transparent')} !important;
  box-shadow: ${(p) => (p.$active ? `inset 3px 0 0 ${BRAND.red}` : 'none')};
`;

const DetailPanel = styled.aside`
  border: 1.5px solid ${BRAND.red};
  border-radius: 12px;
  background: #fff;
  padding: 1.15rem 1.2rem 1.25rem;
  position: sticky;
  top: 1rem;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: #111827;
  }
`;

const CloseX = styled.button`
  appearance: none;
  border: none;
  background: #f3f4f6;
  color: #374151;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  &:hover {
    background: #e5e7eb;
  }
`;

const BuyerBlock = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.85rem;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${BRAND.blue};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
`;

const BuyerName = styled.div`
  font-weight: 700;
  color: #111827;
`;

const BuyerLink = styled.a`
  display: block;
  font-size: 0.85rem;
  color: ${BRAND.red};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const MetaBox = styled.div`
  background: #eff6ff;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  font-size: 0.85rem;
  color: #1e3a5f;
  margin-bottom: 0.85rem;
  line-height: 1.45;
`;

const DetailSection = styled.div`
  margin-bottom: 0.9rem;
  h4 {
    margin: 0 0 0.4rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }
  p {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.45;
  }
`;

const LineList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.875rem;
  li {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.3rem 0;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
  }
`;

const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-weight: 800;
  font-size: 1.05rem;
  color: ${BRAND.red};
`;

const Input = styled.input`
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 100%;
  margin-bottom: 0.5rem;
  background: #f0f7ff;
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 100%;
  min-height: 72px;
  margin-bottom: 0.5rem;
  background: #f0f7ff;
  resize: vertical;
  font-family: inherit;
`;

const Select = styled.select`
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 100%;
  margin-bottom: 0.5rem;
  background: #fff;
`;

const Button = styled.button`
  padding: 0.7rem 1.1rem;
  border-radius: 8px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? '#fff' : BRAND.red)};
  color: ${(p) => (p.$secondary ? '#111827' : '#fff')};
  border: ${(p) => (p.$secondary ? '1px solid #d1d5db' : 'none')};
  margin-right: 0.5rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
  margin: 0.5rem 0 0.25rem;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
  background: ${(p) => {
    if (p.$status === 'cancelled') return '#fee2e2';
    if (p.$status === 'shipped' || p.$status === 'completed' || p.$status === 'ready_for_pickup')
      return '#d1fae5';
    if (p.$status === 'preparing') return '#fef3c7';
    return '#e0e7ff';
  }};
  color: ${(p) => {
    if (p.$status === 'cancelled') return '#991b1b';
    if (p.$status === 'shipped' || p.$status === 'completed' || p.$status === 'ready_for_pickup')
      return '#065f46';
    if (p.$status === 'preparing') return '#92400e';
    return '#3730a3';
  }};
`;

const STATUS_OPTIONS = [
  'pending',
  'preparing',
  'shipped',
  'ready_for_pickup',
  'completed',
  'cancelled',
];

const TAB_DEFS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'cancelled', label: 'Cancelled' },
];

function formatNationality(o) {
  const parts = [o.buyerNationalityPrimary, o.buyerNationalitySecondary].filter(Boolean);
  return parts.length ? parts.join(' / ') : null;
}

function formatKitSize(o) {
  if (o.buyerKitSize) return o.buyerKitSize;
  if (o.buyerTShirtSize && o.buyerShortSize && o.buyerTShirtSize !== o.buyerShortSize) {
    return `Shirt ${o.buyerTShirtSize}, Short ${o.buyerShortSize}`;
  }
  return o.buyerTShirtSize || o.buyerShortSize || null;
}

function buyerName(o) {
  return [o.buyerFirstName, o.buyerLastName].filter(Boolean).join(' ').trim() || o.buyerEmail || '—';
}

function buyerInitials(o) {
  const n = buyerName(o);
  const parts = n.split(/\s+/).filter(Boolean);
  if (!parts.length || n === '—') return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

function itemsSummary(o) {
  const items = Array.isArray(o.items) ? o.items : [];
  if (!items.length) return '—';
  return items.map((i) => i.label).join(', ');
}

function matchesTab(status, tab) {
  if (tab === 'all') return true;
  if (tab === 'pending') return status === 'pending' || status === 'preparing';
  if (tab === 'shipped')
    return status === 'shipped' || status === 'ready_for_pickup' || status === 'completed';
  if (tab === 'cancelled') return status === 'cancelled';
  return true;
}

function dayKey(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const AdminRegistrationOrders = () => {
  const { adminUser } = useAdminAuth();
  const canEdit =
    adminUser?.isSuperAdmin || adminUser?.permissions?.settings_registration_products?.edit;
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [statusTab, setStatusTab] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [editStatus, setEditStatus] = useState('pending');
  const [tracking, setTracking] = useState('');
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const h = consumeAdminNavHandoff();
    if (!h) return;
    if (h.section && h.section !== 'registration-orders') return;
    if (h.registrationOrdersStatus) setStatusTab(String(h.registrationOrdersStatus));
  }, []);

  const { data, isLoading } = useQuery(
    ['admin-registration-orders'],
    () => adminRegistrationOrdersAPI.getAll(),
    { staleTime: 15_000 }
  );
  const orders = useMemo(() => {
    const raw = data?.data || data || [];
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  const pendingCount = orders.filter((o) => matchesTab(o.status, 'pending')).length;
  const shippedCount = orders.filter((o) => matchesTab(o.status, 'shipped')).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (!matchesTab(o.status, statusTab)) return false;
      if (dateFilter && dayKey(o.createdAt) !== dateFilter) return false;
      if (!q) return true;
      const hay = [
        buyerName(o),
        o.buyerEmail,
        o.teamName,
        o.seasonName,
        itemsSummary(o),
        o.trackingNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orders, statusTab, search, dateFilter]);

  const selected = orders.find((o) => o.id === selectedId) || null;

  const openOrder = (o) => {
    setSelectedId(o.id);
    setEditStatus(o.status);
    setTracking(o.trackingNumber || '');
    setNotes(o.fulfillmentNotes || '');
    setMessage(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage(null);
    try {
      const blob = await adminRegistrationOrdersAPI.downloadCsv();
      if (blob && blob.type && String(blob.type).includes('application/json')) {
        const text = await blob.text();
        let msg = 'Export failed';
        try {
          msg = JSON.parse(text).message || msg;
        } catch {
          /* ignore */
        }
        setMessage({ type: 'err', text: msg });
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({
        type: 'err',
        text: err.response?.data?.message || err.message || 'Export failed',
      });
    } finally {
      setExporting(false);
    }
  };

  const saveMutation = useMutation(
    (payload) => adminRegistrationOrdersAPI.update(selectedId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-registration-orders']);
        setMessage({
          type: 'ok',
          text: 'Order updated. Player notified if status changed to shipped / ready for pickup.',
        });
      },
      onError: (err) => {
        setMessage({ type: 'err', text: err.response?.data?.message || 'Update failed' });
      },
    }
  );

  return (
    <Container>
      <Title>Orders</Title>
      <Subtitle>
        Registration product purchases (delivery or pickup). Contact buyers from here; add a tracking
        number when you mark an order as shipped.
      </Subtitle>

      <KpiRow>
        <KpiCard>
          <KpiLabel>Pending orders</KpiLabel>
          <KpiValue>{pendingCount}</KpiValue>
          <KpiHint $alert={pendingCount > 0}>Requires attention</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Shipped packs</KpiLabel>
          <KpiValue>{shippedCount}</KpiValue>
          <KpiHint>Delivered / fulfilled</KpiHint>
        </KpiCard>
      </KpiRow>

      {message && (
        <p style={{ color: message.type === 'err' ? '#b91c1c' : '#047857' }}>{message.text}</p>
      )}

      <FilterRow>
        {TAB_DEFS.map((tab) => (
          <Tab
            key={tab.id}
            type="button"
            $active={statusTab === tab.id}
            onClick={() => setStatusTab(tab.id)}
          >
            {tab.label}
          </Tab>
        ))}
        <SearchInput
          type="search"
          placeholder="Search orders…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search orders"
        />
        <DateInput
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          aria-label="Filter by date"
        />
        <ExportButton type="button" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </ExportButton>
      </FilterRow>

      <Layout $withDetail={!!selected}>
        {isLoading ? (
          <p>Loading…</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Buyer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <ActiveRow
                    key={o.id}
                    $active={selectedId === o.id}
                    onClick={() => openOrder(o)}
                  >
                    <td>
                      {o.createdAt ? new Date(o.createdAt).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{buyerName(o)}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{o.buyerEmail}</div>
                    </td>
                    <td style={{ maxWidth: 220 }}>{itemsSummary(o)}</td>
                    <td>CHF {Number(o.totalChf).toFixed(2)}</td>
                    <td>
                      <StatusPill $status={o.status}>{o.status}</StatusPill>
                    </td>
                  </ActiveRow>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={5} style={{ color: '#6b7280', padding: '1.25rem' }}>
                      No orders match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        )}

        {selected && (
          <DetailPanel>
            <DetailHeader>
              <h3>Order detail</h3>
              <CloseX type="button" aria-label="Close" onClick={() => setSelectedId(null)}>
                ×
              </CloseX>
            </DetailHeader>

            <BuyerBlock>
              <Avatar>{buyerInitials(selected)}</Avatar>
              <div>
                <BuyerName>{buyerName(selected)}</BuyerName>
                {selected.buyerEmail && (
                  <BuyerLink href={`mailto:${selected.buyerEmail}`}>{selected.buyerEmail}</BuyerLink>
                )}
                {selected.buyerPhone && (
                  <BuyerLink href={`tel:${selected.buyerPhone}`}>{selected.buyerPhone}</BuyerLink>
                )}
              </div>
            </BuyerBlock>

            <MetaBox>
              <div>
                <strong>Team:</strong> {selected.teamName || '—'}
              </div>
              <div>
                <strong>Phase:</strong> {selected.seasonName || '—'}
              </div>
            </MetaBox>

            <DetailSection>
              <h4>Player profile (fulfillment)</h4>
              <p>
                <strong>Kit size:</strong> {formatKitSize(selected) || '—'}
                <br />
                <strong>Nationality:</strong> {formatNationality(selected) || '—'}
                <br />
                <strong>Home canton:</strong> {selected.buyerHomeCanton || '—'}
              </p>
            </DetailSection>

            <DetailSection>
              <h4>Shipping address</h4>
              <p>
                <strong>{selected.deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}</strong>
                {selected.deliveryMethod === 'delivery' ? (
                  <>
                    <br />
                    {[
                      selected.streetAddress,
                      [selected.postalCode, selected.city].filter(Boolean).join(' '),
                      selected.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </>
                ) : null}
              </p>
            </DetailSection>

            <DetailSection>
              <h4>Line items</h4>
              <LineList>
                {(Array.isArray(selected.items) ? selected.items : []).map((i, idx) => (
                  <li key={idx}>
                    <span>
                      {i.label} × {i.quantity}
                    </span>
                    <span>CHF {Number(i.lineTotalChf).toFixed(2)}</span>
                  </li>
                ))}
                <li>
                  <span>Subtotal</span>
                  <span>CHF {Number(selected.itemsTotalChf).toFixed(2)}</span>
                </li>
                <li>
                  <span>Delivery fee</span>
                  <span>CHF {Number(selected.deliveryFeeChf).toFixed(2)}</span>
                </li>
              </LineList>
              <TotalLine>
                <span>Total</span>
                <span>CHF {Number(selected.totalChf).toFixed(2)}</span>
              </TotalLine>
            </DetailSection>

            {canEdit && (
              <>
                <Label>Order status</Label>
                <Select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
                <Label>Tracking number</Label>
                <Input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="UPS / DHL / etc."
                />
                <Label>Internal notes</Label>
                <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
                <div style={{ marginTop: '0.75rem' }}>
                  <Button
                    type="button"
                    disabled={saveMutation.isLoading}
                    onClick={() =>
                      saveMutation.mutate({
                        status: editStatus,
                        trackingNumber: tracking,
                        fulfillmentNotes: notes,
                      })
                    }
                  >
                    Save changes
                  </Button>
                  <Button type="button" $secondary onClick={() => setSelectedId(null)}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </DetailPanel>
        )}
      </Layout>
    </Container>
  );
};

export default AdminRegistrationOrders;
